import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Mic, X, Loader2, Ear } from "lucide-react";
import { api } from "../../lib/api";
import { speak as speakBase, stopSpeaking } from "../../lib/speech";

// Every utterance here answers an explicit user command, so it bypasses the
// voice-assist master toggle.
const say = (t) => speakBase(t, { force: true });
import { Switch } from "../ui/switch";
import { useWakePhrase } from "./useWakePhrase";

const WAKE_KEY = "force_wake_phrase";

// Visitor voice guide on the Easy landing: "Hey FORCE" (or tap) opens it; it
// answers app questions aloud and guides people through account creation.
// Voice capture uses the browser's own speech recognition (no account, no keys).
export default function EasyGuestVoice({ onSignup }) {
    const [open, setOpen] = useState(false);
    const [busy, setBusy] = useState(false);
    const [typed, setTyped] = useState("");
    const [listeningCmd, setListeningCmd] = useState(false);
    const [wakeOn, setWakeOn] = useState(() => localStorage.getItem(WAKE_KEY) === "1");
    const recRef = useRef(null);
    const supported = !!(window.SpeechRecognition || window.webkitSpeechRecognition);

    const wakeListening = useWakePhrase(wakeOn, open, () => {
        setOpen(true);
        say("Yes? Ask me anything about FORCE.");
        setTimeout(() => startListening(), 800);
    });

    useEffect(() => () => { stopSpeaking(); recRef.current && recRef.current.abort(); }, []);

    function toggleWake(v) {
        setWakeOn(v);
        localStorage.setItem(WAKE_KEY, v ? "1" : "0");
        if (v) say("Hands-free is on. Just say: Hey FORCE, any time.");
    }

    async function handleCommand(raw) {
        const t = (raw || "").trim();
        if (t.length < 2) { say("Ask me anything about FORCE — or say: sign me up."); return; }
        setBusy(true);
        try {
            const { data } = await api.post("/easy/ask-public", { text: t }, { timeout: 60000 });
            if (data.answer) say(data.answer);
            if (data.action === "signup") onSignup("homeowner");
            else if (data.action === "signup_worker") onSignup("worker");
            else if (data.action === "signup_homeowner") onSignup("homeowner");
            if (data.action && data.action.startsWith("signup")) setOpen(false);
        } catch {
            say("I'm here for anything about FORCE — try asking how it works, or say sign me up!");
        } finally { setBusy(false); }
    }

    function startListening() {
        if (!supported) { say("Voice isn't available in this browser — type your question instead."); return; }
        const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
        const rec = new SR();
        rec.interimResults = false;
        rec.lang = "en-US";
        recRef.current = rec;
        rec.onresult = (e) => {
            const t = e.results[0][0].transcript || "";
            setListeningCmd(false);
            handleCommand(t);
        };
        rec.onerror = () => setListeningCmd(false);
        rec.onend = () => setListeningCmd(false);
        try { rec.start(); setListeningCmd(true); } catch { setListeningCmd(false); }
    }

    return (
        <>
            <button
                data-testid="easy-guest-voice-btn"
                onClick={() => { setOpen(true); say("Ask me anything about FORCE."); }}
                className="flex w-full items-center justify-center gap-[2vw] rounded-3xl border-2 border-dashed border-orange-500/50 bg-orange-500/[0.07] py-[1.2vh] font-bold text-orange-300 transition-colors hover:bg-orange-500/15 text-[clamp(0.95rem,2.6vmin,1.4rem)]"
            >
                <Mic className="h-[clamp(1.3rem,3.5vmin,2rem)] w-[clamp(1.3rem,3.5vmin,2rem)]" />
                Questions? Just ask me
            </button>
            <div className="mt-[1vh] flex items-center justify-center gap-2">
                <Ear className={`h-4 w-4 ${wakeOn ? "text-orange-400" : "text-zinc-500"}`} />
                <span className="font-semibold text-zinc-400 text-[clamp(0.8rem,2vmin,1.1rem)]">Hands-free: say "Hey FORCE"</span>
                <Switch
                    data-testid="easy-guest-wake-toggle"
                    checked={wakeOn}
                    onCheckedChange={toggleWake}
                    className="!h-6 !w-11 !border-2 !border-orange-500/60 data-[state=checked]:!bg-orange-500 data-[state=unchecked]:!bg-zinc-500/70 [&>span]:!h-4 [&>span]:!w-4 [&>span]:!bg-white"
                />
                {wakeOn && (
                    <span data-testid="easy-guest-wake-status" className={`flex items-center gap-1 text-[11px] font-bold ${wakeListening ? "text-emerald-400" : "text-zinc-500"}`}>
                        <span className={`h-2 w-2 rounded-full ${wakeListening ? "bg-emerald-400 animate-pulse" : "bg-zinc-500"}`} />
                        {wakeListening ? "Listening" : "Off"}
                    </span>
                )}
            </div>

            <AnimatePresence>
                {open && (
                    <motion.div
                        data-testid="easy-guest-voice-overlay"
                        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                        className="fixed inset-0 z-[97] flex items-center justify-center bg-black/70 backdrop-blur-sm px-6"
                    >
                        <motion.div
                            initial={{ scale: 0.92, y: 20 }} animate={{ scale: 1, y: 0 }}
                            className="glass w-full max-w-md rounded-3xl border-2 border-orange-500/40 p-6 flex flex-col items-center"
                        >
                            <button data-testid="easy-guest-voice-close" onClick={() => setOpen(false)} className="self-end text-zinc-400 hover:text-white"><X className="h-6 w-6" /></button>
                            <p className="text-center font-display text-[clamp(1.4rem,4.5vmin,2rem)] font-extrabold">Ask me anything…</p>
                            <p className="mt-1 text-center text-zinc-400 text-[clamp(0.95rem,2.5vmin,1.2rem)]">"How does it work?" · "Is it free?" · "Sign me up"</p>

                            {busy ? (
                                <div className="my-6 grid h-24 w-24 place-items-center rounded-full bg-orange-500/15"><Loader2 className="h-10 w-10 animate-spin text-orange-400" /></div>
                            ) : (
                                <button data-testid="easy-guest-voice-mic" onClick={startListening} className={`my-6 grid h-24 w-24 place-items-center rounded-full text-zinc-950 shadow-[0_0_45px_-8px_rgba(255,122,0,0.9)] active:scale-95 transition-transform ${listeningCmd ? "bg-red-500 animate-pulse" : "bg-gradient-to-br from-orange-500 to-amber-500"}`}>
                                    <Mic className="h-10 w-10" />
                                </button>
                            )}
                            <p className="text-sm font-semibold text-zinc-400">{busy ? "One moment…" : listeningCmd ? "Listening… just speak" : supported ? "Tap and ask" : "Voice unavailable here — type below"}</p>

                            <div className="mt-4 flex w-full gap-2">
                                <input
                                    data-testid="easy-guest-voice-input"
                                    value={typed}
                                    onChange={(e) => setTyped(e.target.value)}
                                    onKeyDown={(e) => { if (e.key === "Enter") { handleCommand(typed); setTyped(""); } }}
                                    placeholder="…or type your question"
                                    className="field !rounded-2xl !py-3 !text-lg"
                                />
                                <button data-testid="easy-guest-voice-go" onClick={() => { handleCommand(typed); setTyped(""); }} className="shrink-0 rounded-2xl bg-gradient-to-r from-orange-500 to-amber-500 px-5 text-lg font-extrabold text-zinc-950">Ask</button>
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>
        </>
    );
}
